import * as React from 'react';
import {
    usePatchPayload,
    useAssetCommands,
    AssetCommandDescriptor,
    useProjectId,
    useAssetId,
} from 'owa-timestream-asset-sdk';
import type { PeoplePayload } from '../schema';
import type FindRecipientPersonaType from 'owa-recipient-types/lib/types/FindRecipientPersonaType';
import type RecipientWellWithFindControlViewState from 'owa-recipient-types/lib/types/RecipientWellWithFindControlViewState';
import { useLocalStore } from 'mobx-react-lite';
import createEmptyRecipientWell from 'owa-readwrite-recipient-well/lib/utils/createEmptyRecipientWell';
import { mutatorAction } from 'satcheljs';
import loc from 'owa-localize';
import {
    noPersonName,
    noEmailAddress,
    noTitle,
    noDepartment,
    noBusinessPhoneNumber,
    noOfficeLocation,
} from '../people.locstring.json';
import { Module } from 'owa-bootstrap';
import getModuleSwitcherItemUrl from 'owa-left-rail-utils/lib/getModuleSwitcherItemUrl';
import { ControlIcons } from 'owa-control-icons';
import { openPeopleText } from 'owa-locstrings/lib/strings/openpeople.locstring.json';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { lazyNewMessage } from 'owa-mail-message-actions';
import { focusAsset } from 'owa-timestream-asset-sdk-actions';

interface PeoplePickerInternalViewState extends RecipientWellWithFindControlViewState {
    resetViewState: () => void;
}

export default function usePeopleViewState({ selectedPerson }: PeoplePayload) {
    const patchPayload = usePatchPayload<PeoplePayload>();
    const pickerRef = React.useRef<HTMLDivElement | null>(null);
    const [isPickerVisible, setIsPickerVisible] = React.useState(false);
    const projectId = useProjectId();
    const assetId = useAssetId();

    const openPeople: AssetCommandDescriptor = {
        id: 'openPeople',
        iconName: ControlIcons.People,
        titleResourceId: openPeopleText,
    };

    const viewState: PeoplePickerInternalViewState = useLocalStore(() => ({
        ...createEmptyRecipientWell(),
        resetViewState: () => {
            viewState.queryString = '';
        },
    }));

    const onPersonSelected = React.useCallback(
        (person: FindRecipientPersonaType) => {
            patchPayload({ selectedPerson: person });
            const resetLocalStore = mutatorAction('resetLocalStore', () => {
                viewState.resetViewState();
            });
            resetLocalStore();
            focusAsset(projectId, assetId);
        },
        [viewState, projectId, assetId]
    );

    const removePerson = React.useCallback(() => {
        patchPayload({ selectedPerson: undefined });
        setIsPickerVisible(false);
    }, []);

    React.useEffect(() => {
        document.addEventListener('click', handleOutsideClick);
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [pickerRef.current, selectedPerson?.EmailAddress?.Name]);

    const handleOutsideClick = React.useCallback(
        (event: MouseEvent) => {
            if (!pickerRef.current || pickerRef?.current?.contains(event.target as Node)) {
                return;
            }

            if (isPickerVisible) {
                setIsPickerVisible(false);
            }
        },
        [pickerRef.current, selectedPerson?.EmailAddress?.Name]
    );

    const setPickerVisible = React.useCallback(() => {
        setIsPickerVisible(true);
    }, []);

    useAssetCommands(
        [
            {
                ...openPeople,
                onInvoke: () =>
                    window.open(getModuleSwitcherItemUrl(Module.People).toString(), '_blank'),
                disabled: !selectedPerson?.EmailAddress?.Name,
            },
        ],
        [selectedPerson?.EmailAddress?.Name]
    );

    function onEnterKeyDown(evt: React.KeyboardEvent<unknown>) {
        if (evt.which == KeyCodes.enter) {
            setPickerVisible();
        }
    }

    const composePopoutMail = React.useCallback(() => {
        lazyNewMessage.importAndExecute(
            'ProjectMocaPeopleAsset' /* actionSource */,
            undefined /* targetId */,
            [
                {
                    EmailAddress: selectedPerson?.EmailAddress?.EmailAddress,
                    Name: selectedPerson?.EmailAddress?.Name,
                },
            ] /* toEmailAddressWrappers */,
            undefined /* subject */,
            undefined /* body */,
            true /* isPopout */
        );
    }, [selectedPerson?.EmailAddress?.EmailAddress, selectedPerson?.EmailAddress?.Name]);

    return {
        onPersonSelected,
        removePerson,
        viewState,
        pickerRef,
        isPickerVisible,
        setPickerVisible,
        name: !selectedPerson?.EmailAddress?.Name
            ? loc(noPersonName)
            : selectedPerson.EmailAddress.Name,
        emailAddress: !selectedPerson?.EmailAddress?.EmailAddress
            ? loc(noEmailAddress)
            : selectedPerson.EmailAddress.EmailAddress,
        title: !selectedPerson?.Title ? loc(noTitle) : selectedPerson.Title,
        department: !selectedPerson?.Department ? loc(noDepartment) : selectedPerson.Department,
        phoneNumber: !selectedPerson?.PhoneNumber?.Number
            ? loc(noBusinessPhoneNumber)
            : selectedPerson.PhoneNumber.Number,
        officeLocation: !selectedPerson?.Location ? loc(noOfficeLocation) : selectedPerson.Location,
        onEnterKeyDown,
        composePopoutMail,
    };
}
